import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { passwordSchema as passwordValidator } from "@/lib/passwordValidation";

const formSchema = z.object({
  password: passwordValidator,
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export default function ResetPassword() {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let isMounted = true;
    // ReturnType<typeof setTimeout> — the DOM timer id, not the Node type.
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth event:", event);
      
      if (!isMounted) return;
      
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setIsValidating(false);
        if (timeoutId) clearTimeout(timeoutId);
      }
    });

    // Handle Safari and other browsers that may not auto-process the hash
    const handleRecoveryFromUrl = async () => {
      // Check for tokens in URL hash (e.g., #access_token=...&refresh_token=...)
      const hash = window.location.hash;
      if (hash) {
        const params = new URLSearchParams(hash.substring(1));
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const type = params.get('type');
        
        // If we have tokens and it's a recovery type, manually set the session
        if (accessToken && refreshToken && type === 'recovery') {
          console.log("Found recovery tokens in URL, setting session manually (Safari fix)");
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (!isMounted) return;
          
          if (error) {
            console.error("Error setting session from URL tokens:", error);
            toast.error("Invalid or expired reset link. Please request a new one.");
            navigate("/auth");
            return;
          }
          
          if (data.session) {
            // Clear the hash from the URL for cleaner UX
            window.history.replaceState(null, '', window.location.pathname);
            setIsValidating(false);
            return;
          }
        }
      }
      
      // Check for existing session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!isMounted) return;
      
      if (session) {
        setIsValidating(false);
      } else {
        // Poll for the session instead of one blind 3-second check.
        //
        // lib/auth.tsx does a HARD `window.location.href = "/reset-password"` on
        // PASSWORD_RECOVERY, which reloads this page — so the remounted component
        // never receives that event and always fell through to the timer. If the
        // session hadn't been restored from storage within exactly 3s, a valid
        // reset link was rejected. Now we resolve as soon as the session appears
        // and only give up after the full window has elapsed.
        const POLL_MS = 250;
        const MAX_WAIT_MS = 5000;
        let waited = 0;

        const poll = async () => {
          if (!isMounted) return;

          const { data: { session: recheckSession } } = await supabase.auth.getSession();
          if (!isMounted) return;

          if (recheckSession) {
            setIsValidating(false);
            return;
          }

          waited += POLL_MS;
          if (waited >= MAX_WAIT_MS) {
            toast.error("Invalid or expired reset link. Please request a new one.");
            navigate("/auth");
            return;
          }

          timeoutId = setTimeout(poll, POLL_MS);
        };

        timeoutId = setTimeout(poll, POLL_MS);
      }
    };

    handleRecoveryFromUrl();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      formSchema.parse({ password, confirmPassword });
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        err.errors.forEach((e) => {
          if (e.path[0]) fieldErrors[e.path[0].toString()] = e.message;
        });
        setErrors(fieldErrors);
        return;
      }
      // Any NON-Zod throw previously fell through to signUp()/signIn() with
      // unvalidated input, bypassing the client password policy and the Privacy
      // Policy checkbox (which has no server-side counterpart). Fail closed.
      console.error("Validation failed:", err);
      toast.error("We couldn't validate your details. Please check and try again.");
      return;
    }

    setIsLoading(true);
    const { error } = await updatePassword(password);
    setIsLoading(false);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Password updated successfully!");
      navigate("/dashboard");
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary">EAB Connect</h1>
          <p className="text-muted-foreground mt-2">Reset Your Password</p>
        </div>

        <Card className="shadow-lg">
          <form onSubmit={handleSubmit}>
            <CardHeader>
              <CardTitle>Set New Password</CardTitle>
              <CardDescription>Enter your new password below</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={errors.password ? "border-destructive" : ""}
                />
                {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={errors.confirmPassword ? "border-destructive" : ""}
                />
                {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
              </div>
            </CardContent>
            <CardFooter>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Update Password
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
